
import Vue from "vue";
import Common from "@/mixins/common";
import ShowDialogs from "@/mixins/showDialogs";
import Api from "@/mixins/api";
import Login from "@/models/Login";
import IntentionMessageDialog from "@/views/dialogs/IntentionMessageDialog.vue";

export default Vue.extend({
  name: "AttendanceIntention",
  mixins: [Common, ShowDialogs, Api],
  data() {
    return {
      processing: false
    };
  },
  methods: {
    // 案内メッセージを表示する（閉じるボタンのみ）。
    // 管理画面共通の $info ではなく、スマートフォンでの視認性を優先して
    // 文字を大きくした本画面専用のダイアログ（IntentionMessageDialog）を使用する。
    info(message: string): Promise<boolean> {
      return this.$showDialog<boolean>(IntentionMessageDialog, {
        message,
        title: "出勤意思確認",
        mode: "info"
      });
    },
    // 確認メッセージを表示する（はい／いいえ）。info() と同様、本画面専用の
    // 大きな文字のダイアログ（IntentionMessageDialog）を使用する。
    confirmMessage(message: string): Promise<boolean> {
      return this.$showDialog<boolean>(IntentionMessageDialog, {
        message,
        title: "出勤意思確認",
        mode: "confirm"
      });
    },
    // 出勤日(YYYY-MM-DD)の翌日を同形式で返す（夜勤の終了日表示用）
    nextDateString(dateStr: string): string {
      const d = new Date(`${dateStr}T00:00:00`);
      d.setDate(d.getDate() + 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    // 「終了時間：HH:mm」（日マタギの場合のみ、次行に翌日の日付を付記）を組み立てる
    // isNightShiftはBoolean(true/false)ではなく"0"/"1"の文字列。"1"が日マタギを表す。
    endTimeLine(
      attendanceDate: string,
      endTime: string,
      isNightShift: unknown
    ): string {
      let line = `終了時間：${endTime}`;
      if (isNightShift === "1") {
        const indent = "　".repeat(5);
        line += `\n${indent}(${this.nextDateString(attendanceDate)})`;
      }
      return line;
    },
    async readAndConfirm() {
      // 社員QRを読み取る（社員QR = 社員コード@md5(社員コード)。個人ログインの認証情報）
      const qr = await this.$dialog("qr-code-reader", "翌シフト確認");
      if (!qr) return;
      const parts = String(qr).split("@");
      const employeeCode = parts[0];
      const password = parts[1];
      if (!employeeCode || !password) {
        this.$error("社員QRコードの形式が正しくありません。");
        return;
      }

      this.processing = true;
      try {
        // 社員QRで個人ログイン（ユーザートークンを取得）
        const auth = await ((this as unknown) as {
          $auth: (p: Login) => Promise<{ error?: { errorMessage?: string } }>;
        }).$auth(new Login(employeeCode, password, 2, true));
        if (!auth || auth.error) {
          this.$error(
            (auth && auth.error && auth.error.errorMessage) ||
              "ログインに失敗しました。"
          );
          return;
        }
        this.$store.commit("login", auth);

        // 翌シフト（次回シフト）取得（所属は社員マスタから導出。有給等の非勤務シフトは対象外）
        const res = await this.$post<any>(
          this.Paths.attendanceIntentionNextShift,
          { employeeCode }
        );

        if (!res || res.hasShift !== true) {
          await this.info(
            (res && res.message) || "次回の勤務シフトはありません。"
          );
          return;
        }

        // 同日の全シフト（対象シフト＋他のシフト）を開始時刻順にソートしてから表示する
        const otherShifts: any[] = res.otherShifts || [];
        const allShifts = [
          {
            shiftId: res.shiftId,
            patternName: res.patternName,
            startTime: res.startTime,
            endTime: res.endTime,
            isNightShift: res.isNightShift
          },
          ...otherShifts
        ].sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));

        const shiftsMessage = allShifts
          .map((s, i) => {
            const label = allShifts.length > 1 ? `【シフト${i + 1}】\n` : "";
            return (
              (i === 0 ? "" : "\n\n") +
              label +
              `シフト：${s.patternName || ""}\n` +
              `シフト開始：${s.startTime}\n` +
              this.endTimeLine(res.attendanceDate, s.endTime, s.isNightShift)
            );
          })
          .join("");

        const shiftMessage =
          `出勤日：${res.attendanceDate}\n` +
          `現場名：${res.siteName || ""}\n` +
          shiftsMessage;

        if (res.confirmed) {
          await this.info(
            `${res.employeeName} さん\n（確認済み）\n\n${shiftMessage}`
          );
          return;
        }

        // 翌シフトをダイアログ表示し、「確認」で意思確認フラグを更新
        const message =
          `${res.employeeName} さん\n\n${shiftMessage}\n\n` +
          `この内容で確認しますか？`;

        const ok = await this.confirmMessage(message);
        if (!ok) return;

        await this.$post<any>(this.Paths.attendanceIntentionConfirm, {
          shiftId: res.shiftId,
          otherShiftIds: otherShifts.map(s => s.shiftId)
        });
        await this.info(
          `${res.employeeName} さん\n 次回のシフトを確認しました。`
        );
      } catch (e) {
        this.$error(
          (e as { message?: string }).message || "エラーが発生しました。"
        );
      } finally {
        this.processing = false;
        // 個人トークンを端末に残さないよう、1操作ごとにログアウトして初期状態へ戻す
        this.$store.commit("logout");
      }
    }
  }
});
